import { Stack } from '@mui/material'
import { ColumnTable } from 'arquero'
import { CellInfo } from 'components/cell-visualizations/tsv/types'
import { useMemo } from 'react'
import { CellDataField, PinnedMorphotype } from 'redux/slices'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { getColumn } from '../arrow/arqueroHelpers'
import ContinuousComparisonPlot from './ContinuousComparisonPlot'
import DataCategorySelect from './DataCategorySelect'
import DataFieldSelect from './DataFieldSelect'
import DataTypeCompareSelect from './DataTypeCompareSelect'
import HeatmapComparisonPlot from './HeatmapComparisonPlot'
import useDataCategory, { CELL_GROUPS_CATEGORY_KEY } from './useDataCategory'

export type PlotDatum<T> = { category: string; value: T }

/**
 * Generates plot data for comparing morphotypes based on a specific feature (memory-efficient)
 * Works directly with columnar data without building cellInfo array
 */
export function getPlotDataFromMorphotypesFromDataFrame<T>(
  feature: keyof CellInfo,
  dataFrame: ColumnTable,
  morphotypes: PinnedMorphotype[],
  dataTransform: (value: string) => T,
  prefilteredCellIdSet: Set<string>
): PlotDatum<T>[] {
  const result: PlotDatum<T>[] = []

  // Extract columns once using cached helper
  const cellIds = getColumn<string>(dataFrame, 'CELL_ID')
  const featureValues = getColumn<string | number>(dataFrame, feature as string)

  if (cellIds.length === 0 || featureValues.length === 0) {
    return result
  }

  morphotypes.forEach((morphotype) => {
    const cellIdSet = new Set(morphotype.cells?.points?.map((cell) => cell.cellId))

    for (let i = 0; i < cellIds.length; i += 1) {
      const cellId = cellIds[i]

      // Skip removed cells
      if (!prefilteredCellIdSet.has(cellId) && cellIdSet.has(cellId)) {
        const value = featureValues[i]
        if (value !== undefined && value !== null) {
          result.push({
            category: morphotype.name,
            value: dataTransform(String(value)),
          })
        }
      }
    }
  })

  return result
}

/**
 * DataFrame version of getPlotDataFromCategoryField (memory-efficient)
 * Works directly with columnar data without building cellInfo array
 */
export function getPlotDataFromCategoryFieldFromDataFrame<T>(
  feature: keyof CellInfo,
  category: keyof CellInfo,
  selectedCategoryItems: string[],
  dataFrame: ColumnTable,
  dataTransform: (value: string) => T,
  prefilteredCellIdSet: Set<string>,
  hiddenCellInfoGroupValues: Record<string, string[]>
): PlotDatum<T>[] {
  const result: PlotDatum<T>[] = []

  // Extract columns once using cached helper
  const cellIds = getColumn<string>(dataFrame, 'CELL_ID')
  const categoryValues = getColumn<string | number>(dataFrame, category as string)
  const featureValues = getColumn<string | number>(dataFrame, feature as string)

  if (cellIds.length === 0 || categoryValues.length === 0 || featureValues.length === 0) {
    return result
  }

  // Get hidden attribute columns using cached helper
  const hiddenAttrNames = Object.keys(hiddenCellInfoGroupValues)
  const hiddenAttrColumns = new Map<string, (string | number)[]>()
  hiddenAttrNames.forEach((attrName) => {
    const attrColumn = getColumn<string | number>(dataFrame, attrName)
    if (attrColumn.length > 0) {
      hiddenAttrColumns.set(attrName, attrColumn)
    }
  })

  const selectedCategorySet = new Set(selectedCategoryItems)

  for (let i = 0; i < cellIds.length; i += 1) {
    const cellId = cellIds[i]
    const categoryValue = categoryValues[i]
    const featureValue = featureValues[i]

    // Skip removed cells
    if (!prefilteredCellIdSet.has(cellId)) {
      // Check if filtered (hidden)
      let isFiltered = false
      for (let j = 0; j < hiddenAttrNames.length; j += 1) {
        const attrName = hiddenAttrNames[j]
        const attrColumn = hiddenAttrColumns.get(attrName)
        if (attrColumn) {
          const cellValue = attrColumn[i]
          const hiddenValues = hiddenCellInfoGroupValues[attrName]
          if (hiddenValues?.includes(String(cellValue))) {
            isFiltered = true
            break
          }
        }
      }

      if (
        !isFiltered &&
        categoryValue !== undefined &&
        categoryValue !== null &&
        featureValue !== undefined &&
        featureValue !== null &&
        selectedCategorySet.has(String(categoryValue))
      ) {
        result.push({
          category: String(categoryValue),
          value: dataTransform(String(featureValue)),
        })
      }
    }
  }

  return result
}

const CompareGroups = (): JSX.Element => {
  const {
    cellVisualizations,
    getMergedPinnedMorphotypes,
    dataFrame,
    hiddenCellInfoGroupValues,
    prefilteredCellIdSet,
  } = useCellVisualizationsSlice()

  const { groupCompare } = cellVisualizations

  const { getDataCategoryAttribute } = useDataCategory()

  const {
    selectedFeatures,
    selectedDataCategory,
    selectedDataFields: allSelectedDataFields,
  } = groupCompare

  const selectedGroups = getMergedPinnedMorphotypes().filter((x) => x.isInSelectedGroup)
  const selectedDataFields = allSelectedDataFields[selectedDataCategory]

  const plots = useMemo(() => {
    if (
      !dataFrame ||
      selectedFeatures?.length === 0 ||
      (selectedDataCategory === CELL_GROUPS_CATEGORY_KEY && selectedGroups?.length === 0) ||
      (selectedDataCategory !== CELL_GROUPS_CATEGORY_KEY && selectedDataFields?.length === 0)
    ) {
      return []
    }

    return selectedFeatures.map((dataField: CellDataField): JSX.Element => {
      const feature = dataField.attribute

      let title = `${dataField.label} distribution`
      if (dataField.isContinuous) {
        let data: PlotDatum<number>[] = []
        if (selectedDataCategory === CELL_GROUPS_CATEGORY_KEY) {
          // Use DataFrame version (no cellInfo array needed)
          data = getPlotDataFromMorphotypesFromDataFrame(
            feature as keyof CellInfo,
            dataFrame,
            selectedGroups,
            parseFloat,
            prefilteredCellIdSet
          )
        } else {
          // Use DataFrame version (no cellInfo array needed)
          data = getPlotDataFromCategoryFieldFromDataFrame(
            feature as keyof CellInfo,
            getDataCategoryAttribute(selectedDataCategory as keyof CellInfo),
            selectedDataFields,
            dataFrame,
            parseFloat,
            prefilteredCellIdSet,
            hiddenCellInfoGroupValues
          )
        }
        return (
          <ContinuousComparisonPlot
            key={dataField.attribute}
            data={data}
            title={title}
            width={500}
            height={400}
          />
        )
      }

      title += ` by ${selectedDataCategory}`
      let data: PlotDatum<string>[] = []
      const transform = (value: string) => value
      if (selectedDataCategory === CELL_GROUPS_CATEGORY_KEY) {
        // Use DataFrame version (no cellInfo array needed)
        data = getPlotDataFromMorphotypesFromDataFrame(
          feature as keyof CellInfo,
          dataFrame,
          selectedGroups,
          transform,
          prefilteredCellIdSet
        )
      } else {
        // Use DataFrame version (no cellInfo array needed)
        data = getPlotDataFromCategoryFieldFromDataFrame(
          feature as keyof CellInfo,
          getDataCategoryAttribute(selectedDataCategory as keyof CellInfo),
          selectedDataFields,
          dataFrame,
          transform,
          prefilteredCellIdSet,
          hiddenCellInfoGroupValues
        )
      }
      return (
        <HeatmapComparisonPlot
          key={feature}
          data={data}
          title={title}
          sortValuesNumerically={dataField.isNumerical}
          valueName={dataField.label}
          width={500}
          rowHeight={25}
        />
      )
    })
  }, [selectedDataCategory, selectedDataFields, selectedFeatures, selectedGroups, getDataCategoryAttribute, dataFrame, prefilteredCellIdSet, hiddenCellInfoGroupValues])

  // Reverse the plots list so that as you add more plots, the new plot always shows up at the top
  // where it's immediately visible
  plots.reverse()

  return (
    <Stack spacing={2}>
      <DataCategorySelect />
      <DataTypeCompareSelect />
      <DataFieldSelect />

      <Stack>{plots}</Stack>
    </Stack>
  )
}

export default CompareGroups
